import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from 'store';
import {
  setAvatarDraft,
  setAvatarVariantDraft,
  setAvatarDrafts,
  setFormattedTextDraft,
  setFormattedTextDrafts,
  setProjectDrafts,
  setTextDraft,
} from './ScriptStudioBuffersSlice';

interface AudioPlayerMeta {
  duration: number;
  currentTime: number;
  error: boolean;
  estimatedDuration: number | null;
}

interface ScriptStudioState {
  selectedSectionIds: string[];
  activeSectionId: string | null;
  selectedText: string;
  newlyCreatedSectionIds: string[];
  newlyReorderedSectionIds: string[];
  deletingSectionIds: string[];
  lastUsedAvatar: {
    id: number;
    variantId: number;
    variantVersion?: string | undefined;
  };
  generatingSectionId: string | null;
  streamingSectionIds: string[];
  pendingTakeNameBySectionId: Record<string, string>;
  playingTakeId: string | null;
  playingSectionId: string | null;
  loadingTakeId: string | null;
  preloadedTakeId: string | null;
  audioPlayerMeta: AudioPlayerMeta;
  sectionCollapsedIds: { [sectionId: string]: boolean };
  continuousPlayback: boolean;
  continuousGapActive: boolean;
  reordering: boolean;
  sectionSubmitErrors: { [sectionId: string]: string };
  /**
   * Failed saves of `section.editor`, shown inline on the section until a
   * later save succeeds or the draft is dropped (see `clearSectionSaveErrors`)
   */
  sectionSaveErrors: { [sectionId: string]: string };
  bulkQueuedSectionIds: Record<string, true>;
  bulkGenerateActiveRunId: string | null;
  bulkGenerateSectionErrors: Record<string, string>;
}

const clearErrorsForSections = (
  state: ScriptStudioState,
  sectionIds: string[]
): void => {
  for (const id of sectionIds) {
    // Redux Toolkit allows you to assign values directly to the state in the reducers
    /* eslint-disable no-param-reassign */
    delete state.sectionSubmitErrors[id];
    delete state.bulkGenerateSectionErrors[id];
  }
};

const initialState: ScriptStudioState = {
  selectedSectionIds: [],
  activeSectionId: null,
  selectedText: '',
  newlyCreatedSectionIds: [],
  newlyReorderedSectionIds: [],
  deletingSectionIds: [],
  // for empty projects w/o a voice selected default to Selene (most popular avatar)
  lastUsedAvatar: { id: 24, variantId: 108 },
  generatingSectionId: null,
  streamingSectionIds: [],
  pendingTakeNameBySectionId: {},
  playingTakeId: null,
  playingSectionId: null,
  loadingTakeId: null,
  preloadedTakeId: null,
  audioPlayerMeta: {
    duration: 0,
    currentTime: 0,
    error: false,
    estimatedDuration: null,
  },
  sectionCollapsedIds: {},
  continuousPlayback: true,
  continuousGapActive: false,
  reordering: false,
  sectionSubmitErrors: {},
  sectionSaveErrors: {},
  bulkQueuedSectionIds: {},
  bulkGenerateActiveRunId: null,
  bulkGenerateSectionErrors: {},
};

const scriptStudioSlice = createSlice({
  name: 'scriptStudio',
  initialState,
  reducers: {
    setSectionExpanded: (state, action: PayloadAction<string[]>) => {
      const updated = action.payload.reduce<{ [key: string]: boolean }>(
        (acc, current) => {
          acc[current] = false;
          return acc;
        },
        {}
      );
      return {
        ...state,
        sectionCollapsedIds: {
          ...state.sectionCollapsedIds,
          ...updated,
        },
      };
    },
    setSectionCollapsed: (state, action: PayloadAction<string[]>) => {
      const updated = action.payload.reduce<{ [key: string]: boolean }>(
        (acc, current) => {
          acc[current] = true;
          return acc;
        },
        {}
      );
      return {
        ...state,
        sectionCollapsedIds: {
          ...state.sectionCollapsedIds,
          ...updated,
        },
      };
    },
    setSectionExpandAll: (state, action: PayloadAction) => {
      const result: { [k: string]: boolean } = {};
      for (const k of Object.keys(state.sectionCollapsedIds)) {
        result[k] = false;
      }
      return {
        ...state,
        sectionCollapsedIds: result,
      };
    },
    setSectionCollapseAll: (state, action: PayloadAction) => {
      const result: { [k: string]: boolean } = {};
      for (const k of Object.keys(state.sectionCollapsedIds)) {
        result[k] = true;
      }
      return {
        ...state,
        sectionCollapsedIds: result,
      };
    },
    setSelectedSectionIds: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        selectedSectionIds: action.payload,
      };
    },
    deselectSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        selectedSectionIds: state.selectedSectionIds.filter(
          id => id !== action.payload
        ),
      };
    },
    deselectSections: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        selectedSectionIds: state.selectedSectionIds.filter(
          id => !action.payload.includes(id)
        ),
      };
    },
    setActiveSectionId: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        activeSectionId: action.payload,
      };
    },
    setSelectedText: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        selectedText: action.payload,
      };
    },
    setGeneratingSectionId(state, action: PayloadAction<string | null>) {
      return {
        ...state,
        generatingSectionId: action.payload,
      };
    },
    addStreamingSectionId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        streamingSectionIds: [...state.streamingSectionIds, action.payload],
      };
    },
    removeStreamingSectionId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        streamingSectionIds: state.streamingSectionIds.filter(
          id => id !== action.payload
        ),
      };
    },
    setPendingTakeName: (
      state,
      action: PayloadAction<{ sectionId: string; name: string }>
    ) => {
      return {
        ...state,
        pendingTakeNameBySectionId: {
          ...state.pendingTakeNameBySectionId,
          [action.payload.sectionId]: action.payload.name,
        },
      };
    },
    clearPendingTakeName: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _, ...rest } = state.pendingTakeNameBySectionId;
      return {
        ...state,
        pendingTakeNameBySectionId: rest,
      };
    },
    addNewlyCreatedSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        newlyCreatedSectionIds: [
          ...state.newlyCreatedSectionIds,
          action.payload,
        ],
      };
    },
    removeNewlyCreatedSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        newlyCreatedSectionIds: state.newlyCreatedSectionIds.filter(
          id => id !== action.payload
        ),
      };
    },
    addDeletingSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        deletingSectionIds: [...state.deletingSectionIds, action.payload],
      };
    },
    removeDeletingSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        deletingSectionIds: state.deletingSectionIds.filter(
          id => id !== action.payload
        ),
      };
    },
    addNewlyReorderedSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        newlyReorderedSectionIds: [
          ...state.newlyReorderedSectionIds,
          action.payload,
        ],
      };
    },
    removeNewlyReorderedSection: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        newlyReorderedSectionIds: state.newlyReorderedSectionIds.filter(
          id => id !== action.payload
        ),
      };
    },
    setLastUsedAvatar: (
      state,
      action: PayloadAction<{
        id: number;
        variantId: number;
        variantVersion?: string | undefined;
      }>
    ) => {
      const newAvatar = action.payload ?? null;
      if (
        state.lastUsedAvatar?.id === newAvatar?.id &&
        state.lastUsedAvatar?.variantId === newAvatar?.variantId &&
        state.lastUsedAvatar?.variantVersion === newAvatar?.variantVersion
      ) {
        return state;
      }
      return {
        ...state,
        lastUsedAvatar: newAvatar,
      };
    },
    setPlayingTake: (
      state,
      action: PayloadAction<{
        takeId: string | null;
        sectionId: string | null;
      }>
    ) => {
      return {
        ...state,
        playingTakeId: action.payload.takeId,
        playingSectionId: action.payload.sectionId,
      };
    },
    setLoadingTakeId: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        loadingTakeId: action.payload,
      };
    },
    setPreloadedTakeId: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        preloadedTakeId: action.payload,
      };
    },
    setAudioPlayerMeta: (
      state,
      action: PayloadAction<Partial<AudioPlayerMeta>>
    ) => {
      return {
        ...state,
        audioPlayerMeta: {
          ...state.audioPlayerMeta,
          ...action.payload,
        },
      };
    },
    setContinuousPlayback: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        continuousPlayback: action.payload,
      };
    },
    setContinuousGapActive: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        continuousGapActive: action.payload,
      };
    },
    setReordering: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        reordering: action.payload,
      };
    },
    setSectionSubmitError: (
      state,
      action: PayloadAction<{ sectionId: string; error: string }>
    ) => {
      return {
        ...state,
        sectionSubmitErrors: {
          ...state.sectionSubmitErrors,
          [action.payload.sectionId]: action.payload.error,
        },
      };
    },
    clearSectionSubmitError: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _, ...rest } = state.sectionSubmitErrors;
      return {
        ...state,
        sectionSubmitErrors: rest,
      };
    },
    setSectionSaveErrors: (
      state,
      action: PayloadAction<{ sectionIds: string[]; error: string }>
    ) => {
      const { sectionIds, error } = action.payload;
      for (const id of sectionIds) {
        state.sectionSaveErrors[id] = error;
      }
    },
    clearSectionSaveErrors: (state, action: PayloadAction<string[]>) => {
      for (const id of action.payload) {
        delete state.sectionSaveErrors[id];
      }
    },
    setBulkQueuedSections: (
      state,
      action: PayloadAction<{ sectionIds: string[]; runId: string }>
    ) => {
      const { sectionIds, runId } = action.payload;
      const bulkQueuedSectionIds = sectionIds.reduce<Record<string, true>>(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {}
      );
      const bulkGenerateSectionErrors = { ...state.bulkGenerateSectionErrors };
      for (const id of sectionIds) {
        delete bulkGenerateSectionErrors[id];
      }
      return {
        ...state,
        bulkQueuedSectionIds,
        bulkGenerateActiveRunId: runId,
        bulkGenerateSectionErrors,
      };
    },
    clearBulkQueuedSection: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _, ...rest } = state.bulkQueuedSectionIds;
      const clearedAll = Object.keys(rest).length === 0;
      return {
        ...state,
        bulkQueuedSectionIds: rest,
        bulkGenerateActiveRunId: clearedAll
          ? null
          : state.bulkGenerateActiveRunId,
      };
    },
    clearAllBulkGenerateQueued: state => ({
      ...state,
      bulkQueuedSectionIds: {},
      bulkGenerateActiveRunId: null,
    }),
    setBulkGenerateSectionError: (
      state,
      action: PayloadAction<{ sectionId: string; message: string }>
    ) => {
      const { sectionId, message } = action.payload;
      return {
        ...state,
        bulkGenerateSectionErrors: {
          ...state.bulkGenerateSectionErrors,
          [sectionId]: message,
        },
      };
    },
    clearBulkGenerateSectionError: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _, ...rest } = state.bulkGenerateSectionErrors;
      return {
        ...state,
        bulkGenerateSectionErrors: rest,
      };
    },
    reset: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(setTextDraft, (state, action) => {
        clearErrorsForSections(state, [action.payload.id]);
      })
      .addCase(setFormattedTextDraft, (state, action) => {
        clearErrorsForSections(state, [action.payload.id]);
      })
      .addCase(setFormattedTextDrafts, (state, action) => {
        clearErrorsForSections(
          state,
          action.payload.sections.map(section => section.id)
        );
      })
      .addCase(setAvatarDraft, (state, action) => {
        clearErrorsForSections(state, [action.payload.id]);
      })
      .addCase(setAvatarVariantDraft, (state, action) => {
        clearErrorsForSections(state, [action.payload.id]);
      })
      .addCase(setAvatarDrafts, (state, action) => {
        clearErrorsForSections(
          state,
          action.payload.sections.map(section => section.id)
        );
      })
      .addCase(setProjectDrafts, (state, action) => {
        clearErrorsForSections(
          state,
          action.payload.sections.map(section => section.id)
        );
      });
  },
});

export const setSectionExpandedById = createAsyncThunk<
  void,
  any,
  { state: AppState }
>('scriptStudio/setSectionExpanded', (payload: string, { dispatch }) => {
  dispatch(scriptStudioSlice.actions.setSectionExpanded([payload]));
});

export const selectHasActiveBulkGenerate = (state: AppState) =>
  state.scriptStudio.bulkGenerateActiveRunId != null;

export const {
  setSelectedSectionIds,
  deselectSection,
  deselectSections,
  setActiveSectionId,
  setSelectedText,
  addNewlyCreatedSection,
  removeNewlyCreatedSection,
  addDeletingSection,
  removeDeletingSection,
  setLastUsedAvatar,
  setGeneratingSectionId,
  addStreamingSectionId,
  removeStreamingSectionId,
  setPendingTakeName,
  clearPendingTakeName,
  setPlayingTake,
  setLoadingTakeId,
  setPreloadedTakeId,
  setAudioPlayerMeta,
  setContinuousPlayback,
  setContinuousGapActive,
  reset: resetScriptStudioState,
  setSectionExpanded,
  setSectionCollapsed,
  setSectionCollapseAll,
  setSectionExpandAll,
  setReordering,
  setSectionSubmitError,
  clearSectionSubmitError,
  setSectionSaveErrors,
  clearSectionSaveErrors,
  addNewlyReorderedSection,
  removeNewlyReorderedSection,
  setBulkQueuedSections,
  clearBulkQueuedSection,
  clearAllBulkGenerateQueued,
  setBulkGenerateSectionError,
  clearBulkGenerateSectionError,
} = scriptStudioSlice.actions;

export default scriptStudioSlice;
